import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft, Shield, Loader2, Landmark, Smartphone, Receipt as ReceiptIcon,
  Sparkles, TrendingUp, TrendingDown, ChevronRight, History, Info, AlertCircle,
} from "lucide-react";
import { userApi, errMsg } from "@/lib/api";
import { fmtMoney, fmtDate, timeAgo, newRequestId } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Logo, StatusBadge, CopyButton } from "@/components/common";
import { VaultCoin, TiltCard, VaultSparkles, CountUp } from "@/components/vault";
import RateTicker from "@/components/RateTicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";

/* ----------------------------- debounce ----------------------------- */
function useDebounced(value, ms = 320) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/* ------------------------- Confetti burst ------------------------- */
function Confetti({ fire }) {
  if (!fire) return null;
  const colors = ["hsl(44 86% 60%)", "hsl(44 100% 76%)", "hsl(36 72% 44%)", "hsl(230 20% 90%)"];
  const pieces = Array.from({ length: 48 }).map((_, i) => ({
    left: `${(i * 97) % 100}%`,
    bg: colors[i % colors.length],
    duration: `${1200 + ((i * 41) % 1400)}ms`,
    delay: `${(i * 23) % 400}ms`,
    rot: `${(i * 37) % 360}deg`,
  }));
  return (
    <div className="pointer-events-none fixed inset-0 z-50 overflow-hidden">
      {pieces.map((p, i) => (
        <span
          key={i}
          className="confetti"
          style={{
            left: p.left,
            background: p.bg,
            animationDuration: p.duration,
            animationDelay: p.delay,
            transform: `rotate(${p.rot})`,
          }}
        />
      ))}
    </div>
  );
}

/* -------------------- payout method mini-forms -------------------- */
function PayoutForm({ method, value, onChange }) {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  if (method === "upi") {
    return (
      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider text-[hsl(var(--vault-ink-dim))]">UPI ID</Label>
          <Input
            data-testid="sell-upi-id-input"
            placeholder="yourname@upi"
            value={value.upi_id || ""}
            onChange={set("upi_id")}
            className="vault-input num h-11"
            autoComplete="off"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider text-[hsl(var(--vault-ink-dim))]">Receiver name (optional)</Label>
          <Input
            data-testid="sell-upi-name-input"
            placeholder="As per bank"
            value={value.name || ""}
            onChange={set("name")}
            className="vault-input h-11"
          />
        </div>
      </div>
    );
  }
  if (method === "bank") {
    return (
      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider text-[hsl(var(--vault-ink-dim))]">Account holder name</Label>
          <Input
            data-testid="sell-bank-name-input"
            placeholder="Full name as per bank"
            value={value.account_name || ""}
            onChange={set("account_name")}
            className="vault-input h-11"
          />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-[hsl(var(--vault-ink-dim))]">Account number</Label>
            <Input
              data-testid="sell-bank-account-input"
              placeholder="e.g. 123456789012"
              value={value.account_number || ""}
              onChange={(e) => onChange({ ...value, account_number: e.target.value.replace(/\D/g, "") })}
              className="vault-input num h-11"
              inputMode="numeric"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-[hsl(var(--vault-ink-dim))]">IFSC</Label>
            <Input
              data-testid="sell-bank-ifsc-input"
              placeholder="e.g. HDFC0001234"
              value={value.ifsc || ""}
              onChange={(e) => onChange({ ...value, ifsc: e.target.value.toUpperCase() })}
              className="vault-input num h-11 uppercase"
              maxLength={11}
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider text-[hsl(var(--vault-ink-dim))]">Bank name (optional)</Label>
          <Input
            data-testid="sell-bank-bankname-input"
            placeholder="e.g. HDFC Bank"
            value={value.bank_name || ""}
            onChange={set("bank_name")}
            className="vault-input h-11"
          />
        </div>
      </div>
    );
  }
  // imps_note
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wider text-[hsl(var(--vault-ink-dim))]">IMPS / RTGS details</Label>
      <textarea
        data-testid="sell-imps-note-input"
        rows={4}
        placeholder="Share the full payout details (account name, number, IFSC, bank, branch)."
        value={value.note || ""}
        onChange={(e) => onChange({ ...value, note: e.target.value })}
        className="vault-input w-full rounded-xl p-3 text-sm"
      />
      <p className="text-xs text-[hsl(var(--vault-ink-muted))]">We'll follow up on Telegram if we need anything more.</p>
    </div>
  );
}

/* ========================= TRADE PAGE ========================= */
export default function Trade() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const { user, logout, ready } = useAuth();
  const [side, setSide] = useState("buy"); // buy | sell
  const [buyAmt, setBuyAmt] = useState("");
  const [sellAmt, setSellAmt] = useState("");
  const [payoutMethod, setPayoutMethod] = useState("upi");
  const [payout, setPayout] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  const [err, setErr] = useState("");
  const [clientId, setClientId] = useState(newRequestId());

  const walletsQ = useQuery({
    queryKey: ["wallets"],
    queryFn: () => userApi.get("/wallets").then((r) => r.data),
    refetchInterval: 20000,
  });
  const inrBal = walletsQ.data?.find((w) => w.currency === "INR")?.balance ?? 0;
  const usdtBal = walletsQ.data?.find((w) => w.currency === "USDT")?.balance ?? 0;

  // Buy side: INR -> USDT via /exchange/quote
  const buyNum = parseFloat(buyAmt);
  const dBuy = useDebounced(buyNum > 0 ? buyNum : 0, 280);
  const buyQuoteQ = useQuery({
    queryKey: ["buy-quote", dBuy],
    queryFn: () =>
      userApi.post("/exchange/quote", { from_currency: "INR", amount: dBuy }).then((r) => r.data),
    enabled: dBuy > 0,
  });

  // Sell side: USDT -> INR via /sell/quote
  const sellNum = parseFloat(sellAmt);
  const dSell = useDebounced(sellNum > 0 ? sellNum : 0, 280);
  const sellQuoteQ = useQuery({
    queryKey: ["sell-quote", dSell],
    queryFn: () => userApi.post("/sell/quote", { amount_usdt: dSell }).then((r) => r.data),
    enabled: dSell > 0,
  });

  // Rate card (base reference rate)
  const refRateQ = useQuery({
    queryKey: ["ref-rate"],
    queryFn: () =>
      userApi.post("/exchange/quote", { from_currency: "INR", amount: 1000 }).then((r) => r.data),
    refetchInterval: 60000,
  });
  const sellRefQ = useQuery({
    queryKey: ["sell-ref-rate"],
    queryFn: () => userApi.post("/sell/quote", { amount_usdt: 10 }).then((r) => r.data),
    refetchInterval: 60000,
  });

  // History
  const sellsQ = useQuery({
    queryKey: ["my-sells"],
    queryFn: () => userApi.get("/sell", { params: { limit: 10 } }).then((r) => r.data),
    refetchInterval: 25000,
  });
  const exchangesQ = useQuery({
    queryKey: ["exchanges", "trade"],
    queryFn: () => userApi.get("/exchanges", { params: { limit: 10 } }).then((r) => r.data),
    refetchInterval: 25000,
  });

  // Validation
  const buyMin = refRateQ.data?.min_amount ?? 100;
  const sellMin = sellRefQ.data?.min_amount_usdt ?? 5;

  const buyError = useMemo(() => {
    if (!buyAmt) return null;
    if (!(buyNum > 0)) return "Enter a valid amount";
    if (buyNum < buyMin) return `Minimum ${fmtMoney(buyMin, "INR")}`;
    if (buyNum > inrBal) return `Insufficient INR balance`;
    return null;
  }, [buyAmt, buyNum, buyMin, inrBal]);

  const sellError = useMemo(() => {
    if (!sellAmt) return null;
    if (!(sellNum > 0)) return "Enter a valid amount";
    if (sellNum < sellMin) return `Minimum ${fmtMoney(sellMin, "USDT")}`;
    if (sellNum > usdtBal) return `Insufficient USDT balance`;
    return null;
  }, [sellAmt, sellNum, sellMin, usdtBal]);

  const payoutValid = useMemo(() => {
    if (payoutMethod === "upi") return /^[a-zA-Z0-9._\-]{2,50}@[a-zA-Z]{2,30}$/.test((payout.upi_id || "").trim());
    if (payoutMethod === "bank")
      return (
        (payout.account_name || "").trim().length >= 2 &&
        /^\d{6,24}$/.test((payout.account_number || "").trim()) &&
        /^[A-Z]{4}0[A-Z0-9]{6}$/.test((payout.ifsc || "").trim().toUpperCase())
      );
    return (payout.note || "").trim().length >= 4;
  }, [payoutMethod, payout]);

  const canBuy = !!buyNum && !buyError && buyQuoteQ.data && !submitting;
  const canSell = !!sellNum && !sellError && sellQuoteQ.data && payoutValid && !submitting;
  const canSubmit = side === "buy" ? canBuy : canSell;

  const buyUSDT = async () => {
    setSubmitting(true);
    setErr("");
    try {
      const r = await userApi.post("/exchange", {
        from_currency: "INR",
        amount: buyNum,
        client_request_id: clientId,
      });
      setCelebrate(true);
      setTimeout(() => setCelebrate(false), 2200);
      toast.success("Bought USDT", {
        description: `Received ${fmtMoney(r.data.receive_amount, r.data.to_currency)}`,
      });
      setBuyAmt("");
      setClientId(newRequestId());
      qc.invalidateQueries({ queryKey: ["wallets"] });
      qc.invalidateQueries({ queryKey: ["exchanges"] });
      qc.invalidateQueries({ queryKey: ["ledger"] });
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setSubmitting(false);
      setConfirmOpen(false);
    }
  };

  const sellUSDT = async () => {
    setSubmitting(true);
    setErr("");
    try {
      const body = {
        amount_usdt: sellNum,
        payout_method: payoutMethod,
        client_request_id: clientId,
      };
      if (payoutMethod === "upi") body.payout_upi = { upi_id: payout.upi_id.trim(), name: (payout.name || "").trim() || undefined };
      if (payoutMethod === "bank")
        body.payout_bank = {
          account_name: payout.account_name.trim(),
          account_number: payout.account_number.trim(),
          ifsc: payout.ifsc.trim().toUpperCase(),
          bank_name: (payout.bank_name || "").trim() || undefined,
        };
      if (payoutMethod === "imps_note") body.payout_imps_note = { note: payout.note.trim() };
      const r = await userApi.post("/sell", body);
      setCelebrate(true);
      setTimeout(() => setCelebrate(false), 2200);
      toast.success("Sell order placed", {
        description: `${r.data.id}: ${fmtMoney(r.data.inr_payable, "INR")} will be paid out after verification.`,
      });
      setSellAmt("");
      setPayout({});
      setClientId(newRequestId());
      qc.invalidateQueries({ queryKey: ["my-sells"] });
      qc.invalidateQueries({ queryKey: ["wallets"] });
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setSubmitting(false);
      setConfirmOpen(false);
    }
  };

  const onConfirm = () => (side === "buy" ? buyUSDT() : sellUSDT());

  if (!ready) {
    return (
      <div className="vault flex min-h-screen items-center justify-center text-sm text-[hsl(var(--vault-ink-dim))]">
        Loading the vault…
      </div>
    );
  }
  if (!user) {
    nav("/login?next=/trade", { replace: true });
    return null;
  }

  return (
    <div className="vault noise min-h-screen">
      {/* --- Header --- */}
      <header className="sticky top-0 z-30 border-b border-[hsl(var(--vault-gold)/0.18)] bg-[hsl(230_32%_7%/0.75)] backdrop-blur">
        <div className="mx-auto flex h-16 max-w-6xl items-center gap-4 px-4 sm:px-6 lg:px-8">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-[hsl(var(--vault-ink-dim))] hover:text-white" data-testid="trade-back-link">
            <ArrowLeft className="h-4 w-4" /> Vault
          </Link>
          <div className="hidden sm:block">
            <Logo dark />
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span className="vault-chip hidden sm:inline-flex" data-testid="trade-verified-pill">
              <Shield className="h-3 w-3" /> Team verified
            </span>
            <Button variant="ghost" className="text-[hsl(var(--vault-ink-dim))] hover:text-white" onClick={() => { logout(); nav("/"); }} data-testid="trade-logout">
              Sign out
            </Button>
          </div>
        </div>
        <div className="mx-auto flex max-w-6xl items-center px-4 pb-2 sm:px-6 lg:px-8">
          <RateTicker className="w-full" />
        </div>
      </header>

      <div className="relative mx-auto max-w-6xl px-4 pb-24 pt-10 sm:px-6 lg:px-8">
        <VaultSparkles />

        {/* --- Hero --- */}
        <div className="relative grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <span className="vault-chip" data-testid="trade-badge">
              <Sparkles className="h-3 w-3" /> The Gold Vault
            </span>
            <h1 className="vault-display mt-4 text-4xl font-semibold sm:text-5xl">
              Place your bet on <span className="vault-gold-grad">INR ↔ USDT</span>
            </h1>
            <p className="mt-3 max-w-xl text-sm text-[hsl(var(--vault-ink-dim))] sm:text-base">
              Buy USDT instantly from your INR balance, or cash out USDT to any UPI, bank account or IMPS/RTGS destination. Team-verified payouts within 15 minutes, ledger-locked balances.
            </p>
            <div className="mt-6 flex flex-wrap gap-4 text-sm">
              <div className="rounded-xl border border-[hsl(var(--vault-gold)/0.18)] bg-[hsl(var(--vault-panel)/0.6)] px-4 py-2">
                <p className="text-[11px] uppercase tracking-wider text-[hsl(var(--vault-ink-muted))]">Buy rate</p>
                <p className="num mt-0.5 text-lg vault-gold" data-testid="trade-buy-rate">
                  {refRateQ.data ? fmtMoney(refRateQ.data.rate, "INR") : "—"}
                </p>
              </div>
              <div className="rounded-xl border border-[hsl(var(--vault-gold)/0.18)] bg-[hsl(var(--vault-panel)/0.6)] px-4 py-2">
                <p className="text-[11px] uppercase tracking-wider text-[hsl(var(--vault-ink-muted))]">Sell rate</p>
                <p className="num mt-0.5 text-lg vault-gold" data-testid="trade-sell-rate">
                  {sellRefQ.data ? fmtMoney(sellRefQ.data.rate, "INR") : "—"}
                </p>
              </div>
              <div className="rounded-xl border border-[hsl(var(--vault-gold)/0.18)] bg-[hsl(var(--vault-panel)/0.6)] px-4 py-2">
                <p className="text-[11px] uppercase tracking-wider text-[hsl(var(--vault-ink-muted))]">Fee</p>
                <p className="num mt-0.5 text-lg vault-gold">{refRateQ.data?.fee_pct ?? 0}%</p>
              </div>
            </div>
          </div>

          <div className="hidden items-center justify-center lg:col-span-5 lg:flex">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div
                className="absolute left-1/2 top-1/2 -z-10 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full"
                style={{ background: "radial-gradient(circle, hsl(44 86% 60% / 0.4) 0%, transparent 60%)" }}
              />
              <VaultCoin size={240} paused={submitting} />
            </motion.div>
          </div>
        </div>

        {/* --- Main trading card --- */}
        <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-12">
          <TiltCard className="vault-panel p-6 sm:p-8 lg:col-span-7" testId="trade-card">
            {/* Mobile coin */}
            <div className="mx-auto mb-6 flex justify-center lg:hidden">
              <VaultCoin size={120} paused={submitting} />
            </div>

            {/* Balance strip */}
            <div className="mb-6 grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-[hsl(var(--vault-gold)/0.14)] bg-[hsl(var(--vault-bg)/0.5)] p-3">
                <p className="text-[11px] uppercase tracking-wider text-[hsl(var(--vault-ink-muted))]">INR balance</p>
                <p className="num mt-0.5 text-lg font-semibold text-white" data-testid="trade-inr-balance">
                  {fmtMoney(inrBal, "INR")}
                </p>
              </div>
              <div className="rounded-xl border border-[hsl(var(--vault-gold)/0.14)] bg-[hsl(var(--vault-bg)/0.5)] p-3">
                <p className="text-[11px] uppercase tracking-wider text-[hsl(var(--vault-ink-muted))]">USDT balance</p>
                <p className="num mt-0.5 text-lg font-semibold text-white" data-testid="trade-usdt-balance">
                  {fmtMoney(usdtBal, "USDT")}
                </p>
              </div>
            </div>

            {/* Buy / Sell toggle */}
            <Tabs value={side} onValueChange={(v) => { setSide(v); setErr(""); }}>
              <TabsList className="grid h-12 w-full grid-cols-2 bg-[hsl(var(--vault-bg)/0.6)] p-1">
                <TabsTrigger
                  value="buy"
                  data-testid="trade-tab-buy"
                  className="gap-2 data-[state=active]:bg-gradient-to-b data-[state=active]:from-[hsl(44_88%_62%)] data-[state=active]:to-[hsl(42_76%_48%)] data-[state=active]:text-[hsl(230_32%_10%)] data-[state=active]:shadow-[0_6px_20px_hsl(44_86%_50%/0.3)] text-[hsl(var(--vault-ink-dim))]"
                >
                  <TrendingUp className="h-4 w-4" /> Buy USDT
                </TabsTrigger>
                <TabsTrigger
                  value="sell"
                  data-testid="trade-tab-sell"
                  className="gap-2 data-[state=active]:bg-gradient-to-b data-[state=active]:from-[hsl(44_88%_62%)] data-[state=active]:to-[hsl(42_76%_48%)] data-[state=active]:text-[hsl(230_32%_10%)] data-[state=active]:shadow-[0_6px_20px_hsl(44_86%_50%/0.3)] text-[hsl(var(--vault-ink-dim))]"
                >
                  <TrendingDown className="h-4 w-4" /> Sell USDT
                </TabsTrigger>
              </TabsList>

              <div className="mt-6">
                <AnimatePresence mode="wait" initial={false}>
                  {side === "buy" ? (
                    <motion.div
                      key="buy"
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.2 }}
                      className="space-y-5"
                    >
                      <div>
                        <div className="flex items-center justify-between">
                          <Label className="text-xs uppercase tracking-wider text-[hsl(var(--vault-ink-dim))]">You pay (INR)</Label>
                          <button
                            onClick={() => setBuyAmt(String(inrBal))}
                            className="text-xs font-semibold vault-gold hover:underline"
                            data-testid="trade-buy-max"
                          >
                            Max: <span className="num">{fmtMoney(inrBal, "INR")}</span>
                          </button>
                        </div>
                        <div className="relative mt-2">
                          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl text-[hsl(var(--vault-ink-dim))]">₹</span>
                          <Input
                            inputMode="decimal"
                            placeholder="0.00"
                            value={buyAmt}
                            onChange={(e) => setBuyAmt(e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1"))}
                            className="vault-input num h-16 pl-10 pr-20 text-3xl"
                            data-testid="trade-buy-amount-input"
                          />
                          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-semibold text-[hsl(var(--vault-ink-muted))]">
                            INR
                          </span>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {[500, 1000, 5000, 10000].map((v) => (
                            <button
                              key={v}
                              onClick={() => setBuyAmt(String(v))}
                              className="vault-ghost rounded-full px-3 py-1 text-xs"
                              data-testid={`trade-buy-quick-${v}`}
                            >
                              ₹{v.toLocaleString("en-IN")}
                            </button>
                          ))}
                        </div>
                        {buyError && (
                          <p className="mt-2 text-sm text-rose-300" data-testid="trade-buy-error">{buyError}</p>
                        )}
                      </div>

                      <div className="vault-divider" />

                      <div>
                        <Label className="text-xs uppercase tracking-wider text-[hsl(var(--vault-ink-dim))]">You receive (USDT)</Label>
                        <div className="relative mt-2 flex items-center">
                          <div className="num h-16 flex-1 rounded-xl border border-dashed border-[hsl(var(--vault-gold)/0.25)] bg-[hsl(var(--vault-bg)/0.4)] px-4 text-3xl font-semibold text-white flex items-center">
                            {buyQuoteQ.isFetching && !buyQuoteQ.data ? (
                              <Skeleton className="h-9 w-32 bg-[hsl(var(--vault-panel)/0.8)]" />
                            ) : buyQuoteQ.data && buyNum > 0 ? (
                              <CountUp value={buyQuoteQ.data.receive_amount} testId="trade-buy-receive" format={(v) => v.toFixed(4)} />
                            ) : (
                              <span className="text-[hsl(var(--vault-ink-muted))]">0.0000</span>
                            )}
                          </div>
                          <span className="absolute right-4 text-xs font-semibold text-[hsl(var(--vault-ink-muted))]">USDT</span>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3 text-xs text-[hsl(var(--vault-ink-dim))]">
                        <div className="flex justify-between rounded-lg bg-[hsl(var(--vault-bg)/0.5)] px-3 py-2">
                          <span>Rate</span>
                          <span className="num">{refRateQ.data ? fmtMoney(refRateQ.data.rate, "INR") : "—"}</span>
                        </div>
                        <div className="flex justify-between rounded-lg bg-[hsl(var(--vault-bg)/0.5)] px-3 py-2">
                          <span>Fee</span>
                          <span className="num">{buyQuoteQ.data?.fee_pct ?? refRateQ.data?.fee_pct ?? 0}%</span>
                        </div>
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="sell"
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.2 }}
                      className="space-y-5"
                    >
                      <div>
                        <div className="flex items-center justify-between">
                          <Label className="text-xs uppercase tracking-wider text-[hsl(var(--vault-ink-dim))]">You sell (USDT)</Label>
                          <button
                            onClick={() => setSellAmt(String(usdtBal))}
                            className="text-xs font-semibold vault-gold hover:underline"
                            data-testid="trade-sell-max"
                          >
                            Max: <span className="num">{fmtMoney(usdtBal, "USDT")}</span>
                          </button>
                        </div>
                        <div className="relative mt-2">
                          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl text-[hsl(var(--vault-ink-dim))]">₮</span>
                          <Input
                            inputMode="decimal"
                            placeholder="0.00"
                            value={sellAmt}
                            onChange={(e) => setSellAmt(e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1"))}
                            className="vault-input num h-16 pl-10 pr-20 text-3xl"
                            data-testid="trade-sell-amount-input"
                          />
                          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-semibold text-[hsl(var(--vault-ink-muted))]">
                            USDT
                          </span>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {[10, 50, 100, 500].map((v) => (
                            <button
                              key={v}
                              onClick={() => setSellAmt(String(v))}
                              className="vault-ghost rounded-full px-3 py-1 text-xs"
                              data-testid={`trade-sell-quick-${v}`}
                            >
                              ₮{v}
                            </button>
                          ))}
                        </div>
                        {sellError && (
                          <p className="mt-2 text-sm text-rose-300" data-testid="trade-sell-error">{sellError}</p>
                        )}
                      </div>

                      <div className="vault-divider" />

                      <div>
                        <Label className="text-xs uppercase tracking-wider text-[hsl(var(--vault-ink-dim))]">You receive (INR, after fee)</Label>
                        <div className="relative mt-2 flex items-center">
                          <div className="num h-16 flex-1 rounded-xl border border-dashed border-[hsl(var(--vault-gold)/0.25)] bg-[hsl(var(--vault-bg)/0.4)] px-4 text-3xl font-semibold text-white flex items-center">
                            {sellQuoteQ.isFetching && !sellQuoteQ.data ? (
                              <Skeleton className="h-9 w-32 bg-[hsl(var(--vault-panel)/0.8)]" />
                            ) : sellQuoteQ.data && sellNum > 0 ? (
                              <CountUp value={sellQuoteQ.data.inr_payable} testId="trade-sell-receive" format={(v) => "₹" + v.toFixed(2)} />
                            ) : (
                              <span className="text-[hsl(var(--vault-ink-muted))]">₹0.00</span>
                            )}
                          </div>
                          <span className="absolute right-4 text-xs font-semibold text-[hsl(var(--vault-ink-muted))]">INR</span>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-3 text-xs text-[hsl(var(--vault-ink-dim))]">
                        <div className="flex flex-col rounded-lg bg-[hsl(var(--vault-bg)/0.5)] px-3 py-2">
                          <span className="text-[hsl(var(--vault-ink-muted))]">Rate</span>
                          <span className="num mt-0.5">{sellRefQ.data ? fmtMoney(sellRefQ.data.rate, "INR") : "—"}</span>
                        </div>
                        <div className="flex flex-col rounded-lg bg-[hsl(var(--vault-bg)/0.5)] px-3 py-2">
                          <span className="text-[hsl(var(--vault-ink-muted))]">Gross INR</span>
                          <span className="num mt-0.5">
                            {sellQuoteQ.data ? fmtMoney(sellQuoteQ.data.gross_inr, "INR") : "—"}
                          </span>
                        </div>
                        <div className="flex flex-col rounded-lg bg-[hsl(var(--vault-bg)/0.5)] px-3 py-2">
                          <span className="text-[hsl(var(--vault-ink-muted))]">Fee</span>
                          <span className="num mt-0.5">
                            {sellQuoteQ.data ? fmtMoney(sellQuoteQ.data.fee_amount, "INR") : "—"}
                          </span>
                        </div>
                      </div>

                      <div className="vault-divider" />

                      {/* Payout method */}
                      <div>
                        <Label className="text-xs uppercase tracking-wider text-[hsl(var(--vault-ink-dim))]">Payout method</Label>
                        <div className="mt-2 grid grid-cols-3 gap-2">
                          {[
                            { k: "upi", label: "UPI ID", Icon: Smartphone },
                            { k: "bank", label: "Bank A/C", Icon: Landmark },
                            { k: "imps_note", label: "IMPS / RTGS", Icon: ReceiptIcon },
                          ].map((o) => (
                            <button
                              key={o.k}
                              className="vault-ghost flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs"
                              data-active={payoutMethod === o.k}
                              onClick={() => { setPayoutMethod(o.k); setPayout({}); }}
                              data-testid={`trade-payout-method-${o.k}`}
                            >
                              <o.Icon className="h-3.5 w-3.5" /> {o.label}
                            </button>
                          ))}
                        </div>
                        <div className="mt-4">
                          <PayoutForm method={payoutMethod} value={payout} onChange={setPayout} />
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {err && (
                <Alert variant="destructive" className="mt-5 border-rose-400/50 bg-rose-500/10 text-rose-200">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription data-testid="trade-form-error">{err}</AlertDescription>
                </Alert>
              )}

              <button
                className="vault-cta mt-6 h-14 w-full rounded-xl text-base"
                disabled={!canSubmit}
                onClick={() => setConfirmOpen(true)}
                data-testid="trade-submit-button"
              >
                {submitting ? (
                  <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Processing…</span>
                ) : side === "buy" ? (
                  <>Spin to buy USDT <ChevronRight className="inline h-5 w-5" /></>
                ) : (
                  <>Place sell order <ChevronRight className="inline h-5 w-5" /></>
                )}
              </button>

              <p className="mt-3 flex items-start gap-2 text-xs text-[hsl(var(--vault-ink-muted))]">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                {side === "buy"
                  ? "Buy orders settle instantly from your INR balance. Need more INR? Make a deposit first."
                  : "Sell orders are paid out after our team verifies and sends the INR (usually within 15 minutes). USDT is locked immediately."}
              </p>
            </Tabs>
          </TiltCard>

          {/* --- Side panel --- */}
          <div className="space-y-5 lg:col-span-5">
            <TiltCard className="vault-panel p-5" strength={6}>
              <h3 className="vault-display text-xl font-semibold">
                House rules <span className="vault-gold-grad">of the vault</span>
              </h3>
              <ul className="mt-3 space-y-3 text-sm text-[hsl(var(--vault-ink-dim))]">
                <li className="flex gap-3">
                  <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--vault-gold)/0.12)] text-xs font-bold vault-gold">1</span>
                  Rates are set by our team and locked on confirmation.
                </li>
                <li className="flex gap-3">
                  <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--vault-gold)/0.12)] text-xs font-bold vault-gold">2</span>
                  Selling USDT holds the amount instantly. If our team rejects, your USDT is returned.
                </li>
                <li className="flex gap-3">
                  <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--vault-gold)/0.12)] text-xs font-bold vault-gold">3</span>
                  Buy orders settle instantly between your INR and USDT wallets.
                </li>
              </ul>
            </TiltCard>

            <TiltCard className="vault-panel p-5" strength={6} testId="trade-history-card">
              <div className="flex items-center justify-between">
                <h3 className="vault-display text-xl font-semibold">Vault history</h3>
                <Link to="/transactions" className="text-xs vault-gold hover:underline" data-testid="trade-view-all">
                  All transactions
                </Link>
              </div>
              <div className="mt-4 space-y-2">
                <RecentList sells={sellsQ.data?.items || []} exchanges={exchangesQ.data || []} />
              </div>
            </TiltCard>
          </div>
        </div>
      </div>

      {/* --- Confirm dialog --- */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="trade-confirm-dialog" className="border border-[hsl(var(--vault-gold)/0.25)] bg-[hsl(var(--vault-panel))] text-[hsl(var(--vault-ink))]">
          <AlertDialogHeader>
            <AlertDialogTitle className="vault-display">
              {side === "buy" ? "Confirm your bet" : "Confirm sell order"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[hsl(var(--vault-ink-dim))]">
              {side === "buy"
                ? "Instant conversion between your INR and USDT wallets."
                : "USDT will be held immediately. INR is paid out after our team verifies (usually within 15 minutes)."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 rounded-lg bg-[hsl(var(--vault-bg)/0.5)] p-4 text-sm">
            {side === "buy" && buyQuoteQ.data ? (
              <>
                <Row label="You pay" value={<span className="num">{fmtMoney(buyNum, "INR")}</span>} />
                <Row label="Rate" value={<span className="num">{fmtMoney(buyQuoteQ.data.rate, "INR")}</span>} />
                <Row label="Fee" value={<span className="num">{buyQuoteQ.data.fee_pct}%</span>} />
                <div className="vault-divider my-2" />
                <Row label={<span className="font-semibold">You receive</span>}
                  value={<span className="num font-semibold vault-gold" data-testid="trade-confirm-receive">
                    {fmtMoney(buyQuoteQ.data.receive_amount, "USDT")}
                  </span>} />
              </>
            ) : sellQuoteQ.data ? (
              <>
                <Row label="You sell" value={<span className="num">{fmtMoney(sellNum, "USDT")}</span>} />
                <Row label="Rate" value={<span className="num">{fmtMoney(sellQuoteQ.data.rate, "INR")}</span>} />
                <Row label="Fee" value={<span className="num">{fmtMoney(sellQuoteQ.data.fee_amount, "INR")} ({sellQuoteQ.data.fee_pct}%)</span>} />
                <Row label="Payout to" value={
                  <span className="text-right text-xs text-[hsl(var(--vault-ink-dim))]" data-testid="trade-confirm-payout">
                    {payoutMethod === "upi" && payout.upi_id}
                    {payoutMethod === "bank" && `${payout.account_name} • ${payout.account_number} • ${payout.ifsc}`}
                    {payoutMethod === "imps_note" && (payout.note || "").slice(0, 60) + ((payout.note || "").length > 60 ? "…" : "")}
                  </span>
                } />
                <div className="vault-divider my-2" />
                <Row label={<span className="font-semibold">You receive</span>}
                  value={<span className="num font-semibold vault-gold" data-testid="trade-confirm-receive">
                    {fmtMoney(sellQuoteQ.data.inr_payable, "INR")}
                  </span>} />
              </>
            ) : null}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="trade-confirm-cancel" className="border-[hsl(var(--vault-gold)/0.3)] bg-transparent text-[hsl(var(--vault-ink))] hover:bg-[hsl(var(--vault-panel-2))]">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); onConfirm(); }}
              disabled={submitting}
              data-testid="trade-confirm-submit"
              className="vault-cta border-none"
            >
              {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Confetti fire={celebrate} />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[hsl(var(--vault-ink-dim))]">{label}</span>
      <span>{value}</span>
    </div>
  );
}

function RecentList({ sells, exchanges }) {
  // merge, newest first, cap to 7
  const items = [
    ...sells.map((s) => ({
      kind: "sell",
      id: s.id,
      at: s.created_at,
      amount: `${fmtMoney(s.amount_usdt, "USDT")} → ${fmtMoney(s.inr_payable, "INR")}`,
      status: s.status,
    })),
    ...exchanges.map((e) => ({
      kind: "exchange",
      id: e.id,
      at: e.created_at,
      amount: `${fmtMoney(e.amount, e.from_currency)} → ${fmtMoney(e.receive_amount, e.to_currency)}`,
      status: e.status,
    })),
  ]
    .sort((a, b) => new Date(b.at) - new Date(a.at))
    .slice(0, 7);

  if (items.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-[hsl(var(--vault-gold)/0.2)] p-5 text-center text-sm text-[hsl(var(--vault-ink-muted))]" data-testid="trade-history-empty">
        <History className="mx-auto mb-2 h-5 w-5" />
        No trades yet. Make the house an offer.
      </div>
    );
  }
  return (
    <ul className="divide-y divide-[hsl(var(--vault-gold)/0.12)]">
      {items.map((it) => (
        <li key={`${it.kind}:${it.id}`} className="flex items-center gap-3 py-2.5" data-testid="trade-history-item">
          <span className="vault-chip" style={{ minWidth: 60 }}>
            {it.kind === "sell" ? "Sell" : "Buy"}
          </span>
          <div className="min-w-0 flex-1">
            <p className="num text-sm text-white">{it.amount}</p>
            <p className="text-[11px] text-[hsl(var(--vault-ink-muted))]">
              {it.id} · {timeAgo(it.at)}
            </p>
          </div>
          <StatusBadge status={it.status} />
        </li>
      ))}
    </ul>
  );
}
